import React, { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import type { IParticipantHqProfile } from '@/api/ParticipantHqQuery';

interface IParticipantSessionContext {
  email: string;
  profile: IParticipantHqProfile | null;
  fullName: string;
}

const ParticipantSessionContext = createContext<IParticipantSessionContext | null>(null);

interface IParticipantSessionProviderProps {
  email?: string;
  profile?: IParticipantHqProfile | null;
  children: ReactNode;
}

export const ParticipantSessionProvider: React.FC<IParticipantSessionProviderProps> = ({
  email = '',
  profile = null,
  children,
}) => {
  const value = useMemo(
    () => ({
      email,
      profile,
      fullName: profile?.fullName || email,
    }),
    [email, profile]
  );

  return (
    <ParticipantSessionContext.Provider value={value}>
      {children}
    </ParticipantSessionContext.Provider>
  );
};

export const useParticipantSession = (): IParticipantSessionContext => {
  const ctx = useContext(ParticipantSessionContext);

  if (!ctx) {
    throw new Error('useParticipantSession must be used within ParticipantSessionProvider');
  }

  return ctx;
};

ParticipantSessionProvider.displayName = 'ParticipantSessionProvider';
